import { useCallback, useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../lib/api';
import type { GroupConversation, TaskConversation } from '../types/conversations';

// Mirrors ceben-mobile/src/hooks/use-conversations.ts: the two
// conversations endpoints are independent resources, so they are
// fetched in parallel. Web naming follows the existing camelCase
// convention (authService.ts) rather than mobile's kebab-case.
// `pollMs` keeps the lists (and the unread badge derived from them) fresh
// without a socket subscription: refetch on an interval and when the tab
// becomes visible again. Polls are silent (no loading flicker).
export function useConversations(pollMs?: number) {
  const [taskConversations, setTaskConversations] = useState<TaskConversation[]>([]);
  const [groupConversations, setGroupConversations] = useState<GroupConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConversations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [tasksRes, groupsRes] = await Promise.all([
        api.get('/tasks/conversations'),
        api.get('/workspace/groups/conversations')
      ]);
      setTaskConversations(tasksRes.data.data ?? []);
      setGroupConversations(groupsRes.data.data ?? []);
      setLoaded(true);
    } catch (err) {
      if (!silent) setError(getApiErrorMessage(err, 'Failed to load conversations'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!pollMs) return;
    const tick = () => { if (document.visibilityState === 'visible') fetchConversations(true); };
    const timer = window.setInterval(tick, pollMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [pollMs, fetchConversations]);

  const refetch = useCallback(() => fetchConversations(), [fetchConversations]);

  return { taskConversations, groupConversations, loading, loaded, error, refetch };
}
